import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, UserPlus, Eye, CalendarDays, Sparkles, Bell, CreditCard, RotateCcw, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    getNotifications,
    markAllNotifsRead,
    markNotifRead,
    seedUserDataOnce,
} from "@/lib/socialStore";
import { MOCK_PEOPLE } from "@/lib/mockData";
import { useSocialVersion, timeAgo } from "@/lib/hooks";

const ICONS = {
    message:  { icon: MessageCircle, tone: "primary" },
    follower: { icon: UserPlus,      tone: "primary" },
    view:     { icon: Eye,           tone: "primary" },
    reminder: { icon: CalendarDays,  tone: "primary" },
    welcome:  { icon: Sparkles,      tone: "primary" },
    boost:    { icon: Sparkles,      tone: "primary" },
    payment:  { icon: CreditCard,    tone: "primary" },
    refund:   { icon: RotateCcw,     tone: "primary" },
    review:   { icon: Star,          tone: "primary" },
};

// Fallback routes when a notification doesn't have an explicit link.
const FALLBACK_LINK = {
    message:  "/messages",
    follower: "/profile",
    view:     "/profile",
    reminder: "/home",
    welcome:  "/profile/edit",
    boost:    "/insights",
    payment:  "/wallet/transactions",
    refund:   "/wallet",
    review:   "/profile",
};

const Notifications = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const v = useSocialVersion();

    useEffect(() => {
        if (user) seedUserDataOnce(user.id, MOCK_PEOPLE);
    }, [user]);

    const list = useMemo(
        () => (user ? getNotifications(user.id) : []),
        [user, v]
    );
    const hasUnread = list.some((n) => !n.read);

    return (
        <div className="min-h-full animate-fade-in" data-testid="notifications-screen">
            <header className="sticky top-0 z-10 px-5 pt-[max(14px,env(safe-area-inset-top))] pb-3 bg-background/90 backdrop-blur border-b border-border/60">
                <div className="flex items-center justify-between">
                    <h1 className="font-display text-[22px] font-semibold tracking-tight">
                        Notifications
                    </h1>
                    <button
                        data-testid="notifications-mark-all"
                        onClick={() => user && markAllNotifsRead(user.id)}
                        disabled={!hasUnread}
                        className="text-[12.5px] font-medium text-primary disabled:text-muted-foreground"
                    >
                        Mark all read
                    </button>
                </div>
            </header>

            {list.length === 0 ? (
                <EmptyState />
            ) : (
                <ul className="px-3 py-2 space-y-1.5" data-testid="notifications-list">
                    {list.map((n) => {
                        const def = ICONS[n.type] || ICONS.welcome;
                        const Icon = def.icon;
                        const target = n.link || FALLBACK_LINK[n.type] || null;
                        const handleClick = () => {
                            if (user) markNotifRead(user.id, n.id);
                            if (target) navigate(target);
                        };
                        return (
                            <li key={n.id}>
                                <button
                                    data-testid={`notif-item-${n.id}`}
                                    onClick={handleClick}
                                    className={[
                                        "w-full flex items-start gap-3 p-3 rounded-2xl border text-left transition-colors",
                                        n.read
                                            ? "border-transparent bg-transparent hover:bg-accent/40"
                                            : "border-primary/20 bg-primary/[0.06]",
                                    ].join(" ")}
                                >
                                    <span
                                        className="h-11 w-11 shrink-0 rounded-full flex items-center justify-center text-primary"
                                        style={{
                                            background:
                                                "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.06) 60%, transparent 100%)",
                                        }}
                                    >
                                        <Icon size={18} strokeWidth={2.2} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-display text-[13.5px] font-semibold truncate">
                                                {n.title}
                                            </span>
                                            {!n.read && (
                                                <span
                                                    data-testid={`notif-dot-${n.id}`}
                                                    className="h-2 w-2 rounded-full bg-primary shrink-0"
                                                    aria-hidden
                                                />
                                            )}
                                        </div>
                                        <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-snug">
                                            {n.body}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground mt-1">
                                            {timeAgo(n.at)}
                                        </p>
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const EmptyState = () => (
    <div className="pt-20 text-center px-6" data-testid="notifications-empty">
        <div
            className="mx-auto h-14 w-14 rounded-full flex items-center justify-center"
            style={{
                background:
                    "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.06) 60%, transparent 100%)",
            }}
        >
            <Bell size={22} className="text-primary" />
        </div>
        <p className="mt-4 font-display text-[15px] font-semibold">You're all caught up</p>
        <p className="text-[12.5px] text-muted-foreground mt-1">
            New activity will show up here.
        </p>
    </div>
);

export default Notifications;
